// The action cache is a single browser-session cache of the entire action graph, based on the group cache.
// It is used to speed up fetching and checking for handlers and/or individual action graph nodes.

// Unlike the way we store action graphs in the backend, we store all action nodes in a single graph cache. Because our nodes have unique graph IDs and
// we are not allowed to link edges between nodes on different graphs, we can be sure that this is safe.

// In general, for our action editor we always fetch directly from API and not from our cache -- this is only used for execution.
// This also means we don't support changes -- only refreshing the cache directly from source.

import { inject, ref } from 'vue';
import { TRIGGER_TYPES } from './composables/useActionEngine';

const ActionCache = {
	_nodeCache: ref({}),		// Cache of action nodes.
	_edgeCache: ref({}),		// Cache of action edges.
	_graphCache: ref({}),		// Cache of action graphs metadata only.
	_promise: null,				// Promise that resolves when the action cache is ready.
	$http: null,				// HTTP client to use for fetching action list. Set the first time fetchActionGraph is called - which must be in a setup().
	
	// Called initially by App component to fetch action graph from API backend, once or on manual refresh.
	fetchActionGraph() {
		if (!this.$http)
			this.$http = inject('$http');

		this._promise = new Promise(resolve => {
			// TODO: Error handling for this call!
			this.$http.get('/action').then(response => {
				this._nodeCache.value = {};
				this._edgeCache.value = {};
				this._graphCache.value = {};
				for (const graph of response.data) {
					// Add nodes and edges to their respective caches.
					for (const node of graph.nodes) {
						this._nodeCache.value[node.id] = node;
					}
					for (const edge of graph.edges) {
						this._edgeCache.value[edge.id] = edge;
					}
					// Add graph metadata, removing nodes/edges but storing details on the trigger node.
					graph['_trigger'] = graph.nodes.find(node => node.type && TRIGGER_TYPES[node.type.toUpperCase()]);
					delete graph.nodes;
					delete graph.edges;
					this._graphCache.value[graph.id] = graph;
				}
				resolve();
			}).catch(error => {
				console.error('Error fetching action graph:', error);
			});
		});
	},

	// Await'ed by other components to wait for cache ready. Note that this MUST be done in a setup or later
	// lifecycle hook so App base component has a chance to call fetchActionGraph to setup the promise first!
	waitForData() {
		return this._promise;
	},

	// Get metadata for all graphs in the cache, for use in editor selection interfaces.
	getGraphsMeta() {
		return this._graphCache.value;
	},
	// Fetch the trigger node details for a specific graph ID.
	getTriggerForGraphId(graphId) {
		if (!this._graphCache.value[graphId])
			return null;
		return this._graphCache.value[graphId]._trigger;
	},

	// Get caches of all nodes and edges, for use in graph execution.
	getNodes() {
		return this._nodeCache.value;
	},
	getEdges() {
		return this._edgeCache.value;
	},

	// Check if we have any triggers for a specific event quickly, returning false or an array of graph metadata.
	getTriggers(event_type) {
		if (typeof event_type !== 'string')
			return false;
		event_type = event_type.toUpperCase();
		const triggers = [];
		const cache = this._graphCache.value;
		for (const key in cache) {
			const graph = cache[key];
			if (graph._trigger && graph._trigger.type && graph._trigger.type.toUpperCase() === event_type) {
				triggers.push(graph);
			}
		}
		return triggers.length > 0 ? triggers : false;
	},
};

export { ActionCache };